/**
 * Overlay de pantalla completa dentro del navegador (no Fullscreen API del SO).
 * Se ancla a ion-app (no a body) para que los popovers de Ionic en #overlay-container
 * queden por encima con su --z-index.
 */

/** Clase CSS para popovers (información, select, etc.) sobre el overlay de gráficos. */
export const CSS_CLASS_POPOVER_GRAFICO_FULLSCREEN = 'popover-grafico-fullscreen';

export interface AnclaDomGrafico {
  padre: HTMLElement;
  siguiente: Node | null;
}

function contenedorOverlayGrafico(): HTMLElement {
  return document.querySelector('ion-app') ?? document.body;
}

function overlayActivoEnContenedor(root: HTMLElement): boolean {
  return (
    root.classList.contains('grafico-overlay-activo') &&
    root.parentElement === contenedorOverlayGrafico()
  );
}

/** Overlay huérfano en ion-app tras reconciliación de Angular al abrir pantalla completa. */
export function buscarRootOverlayGraficoHuerfano(): HTMLElement | null {
  return document.querySelector(
    'ion-app > .grafico-pantalla-completa-root.grafico-overlay-activo'
  ) as HTMLElement | null;
}

export function activarOverlayPantallaCompleta(root: HTMLElement): AnclaDomGrafico | null {
  const padre = root.parentElement;
  if (!padre) {
    return null;
  }

  const ancla: AnclaDomGrafico = {
    padre,
    siguiente: root.nextSibling
  };

  contenedorOverlayGrafico().appendChild(root);
  root.classList.add('grafico-overlay-activo');
  document.body.classList.add('grafico-fullscreen-activo');
  return ancla;
}

export function desactivarOverlayPantallaCompleta(
  root: HTMLElement,
  ancla: AnclaDomGrafico | null
): void {
  root.classList.remove('grafico-overlay-activo');
  document.body.classList.remove('grafico-fullscreen-activo');

  if (!ancla) {
    return;
  }

  if (ancla.siguiente) {
    ancla.padre.insertBefore(root, ancla.siguiente);
  } else {
    ancla.padre.appendChild(root);
  }
}

/**
 * Reaplica el overlay tras el render de Angular (p. ej. al montar la tabla o al cambiar popovers).
 * Evita perder `grafico-overlay-activo` y los estilos de pantalla completa.
 */
export function sincronizarOverlayPantallaCompleta(
  root: HTMLElement | null | undefined,
  modalAbierto: boolean,
  ancla: AnclaDomGrafico | null
): AnclaDomGrafico | null {
  if (!modalAbierto || !root) {
    return ancla;
  }

  if (overlayActivoEnContenedor(root)) {
    return ancla;
  }

  const huerfano = buscarRootOverlayGraficoHuerfano();
  if (huerfano && huerfano !== root) {
    desactivarOverlayPantallaCompleta(huerfano, ancla);
  }

  return activarOverlayPantallaCompleta(root);
}

/** Cierra el overlay aunque Angular haya recreado el nodo del template. */
export function cerrarOverlayPantallaCompleta(
  root: HTMLElement | null | undefined,
  ancla: AnclaDomGrafico | null
): void {
  const objetivo = buscarRootOverlayGraficoHuerfano() ?? root;
  if (objetivo) {
    desactivarOverlayPantallaCompleta(objetivo, ancla);
    return;
  }

  document.body.classList.remove('grafico-fullscreen-activo');
}
